import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  getWorkspacePulse,
  subscribeWorkspacePulseEvents,
  type WorkspacePulseEvent,
  type WorkspacePulseSnapshot,
} from '../services/aiAgentBridge'
import {
  deletePulseReport,
  drainQueuedPulseReports,
  pinPulseReport,
  runPulseReport,
  subscribePulseReports,
} from '../services/pulseReportService'
import { useAccessRestriction } from '../hooks/useAccessRestriction'
import type { PulseReport, PulseReportKind } from '../types/pulseReport'
import {
  emptyPulseSnapshot,
  MemberPulseContext,
  type MemberPulseContextValue,
} from './memberPulseContextCore'

export const MemberPulseProvider = ({
  workspaceId = '',
  uid = '',
  walletCredits = 0,
  children,
}: {
  workspaceId?: string
  uid?: string
  walletCredits?: number
  children: ReactNode
}) => {
  const [rawPulse, setRawPulse] = useState<WorkspacePulseSnapshot | null>(null)
  const [prevWorkspaceId, setPrevWorkspaceId] = useState(workspaceId)
  if (prevWorkspaceId !== workspaceId) {
    setPrevWorkspaceId(workspaceId)
    setRawPulse(null)
  }

  const pulse = useMemo<WorkspacePulseSnapshot>(() => {
    const base = (rawPulse && rawPulse.workspaceId === workspaceId) ? rawPulse : emptyPulseSnapshot(workspaceId, walletCredits)
    return {
      ...base,
      walletCredits,
    }
  }, [rawPulse, workspaceId, walletCredits])

  const [reports, setReports] = useState<PulseReport[]>([])
  const [events, setEvents] = useState<WorkspacePulseEvent[]>([])
  const [error, setError] = useState<string | null>(null)
  const [reportsUnavailable, setReportsUnavailable] = useState(false)
  const { restricted } = useAccessRestriction()

  const refreshPulse = useCallback(() => {
    if (!workspaceId || restricted) return
    setError(null)
    void getWorkspacePulse(workspaceId)
      .then((snapshot) => setRawPulse(snapshot))
      .catch((caught) => {
        setError(caught instanceof Error ? caught.message : 'Pulse refresh failed.')
      })
  }, [workspaceId, restricted])

  // Refetch when workspaceId changes
  useEffect(() => {
    if (!workspaceId || restricted) return
    let isCancelled = false
    void getWorkspacePulse(workspaceId)
      .then((snapshot) => {
        if (!isCancelled) setRawPulse(snapshot)
      })
      .catch((caught) => {
        if (!isCancelled) {
          setError(caught instanceof Error ? caught.message : 'Pulse refresh failed.')
        }
      })
    return () => {
      isCancelled = true
    }
  }, [workspaceId, restricted])

  // Live feed events subscription
  useEffect(() => {
    if (!workspaceId || restricted) return
    return subscribeWorkspacePulseEvents(
      workspaceId,
      setEvents,
      () => setEvents([])
    )
  }, [workspaceId, restricted])

  // Pulse reports subscription
  useEffect(() => {
    if (!workspaceId || !uid) return
    return subscribePulseReports(
      workspaceId,
      uid,
      setReports,
      (caught) => {
        const code = typeof caught === 'object' && caught && 'code' in caught ? String(caught.code) : ''
        if (code === 'permission-denied') {
          setReportsUnavailable(true)
          return
        }
        setError(caught instanceof Error ? caught.message : 'Pulse report subscription failed.')
      }
    )
  }, [uid, workspaceId])

  // Drain queued reports
  useEffect(() => {
    if (!workspaceId) return
    void drainQueuedPulseReports(workspaceId)
  }, [workspaceId])

  const pinReport = useCallback(async (reportId: string, pinned: boolean) => {
    if (!workspaceId) return
    await pinPulseReport(workspaceId, reportId, pinned)
  }, [workspaceId])

  const deleteReport = useCallback(async (reportId: string) => {
    if (!workspaceId) return
    await deletePulseReport(workspaceId, reportId)
  }, [workspaceId])

  const runReport = useCallback(async (kind: PulseReportKind) => {
    if (!workspaceId) throw new Error('Workspace is not available')
    return runPulseReport({ workspaceId, kind, inputs: { windowMs: 24 * 60 * 60 * 1000 } })
  }, [workspaceId])

  const value = useMemo<MemberPulseContextValue>(() => ({
    pulse,
    reports,
    events,
    error,
    reportsUnavailable,
    refreshPulse,
    setError,
    pinReport,
    deleteReport,
    runReport,
  }), [
    pulse,
    reports,
    events,
    error,
    reportsUnavailable,
    refreshPulse,
    pinReport,
    deleteReport,
    runReport,
  ])

  return (
    <MemberPulseContext.Provider value={value}>
      {children}
    </MemberPulseContext.Provider>
  )
}
